import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown, Users, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * TeamMemberSelect - A combo box that allows selecting from team members or typing a custom name
 * 
 * Props:
 * - value: string - current value
 * - onChange: (value: string) => void - callback when value changes
 * - teamMembers: Array<{id, name, role}> - list of team members from project
 * - placeholder: string - placeholder text
 * - className: string - additional CSS classes
 * - onManageTeam: () => void - callback to open team management
 * - showManageLink: boolean - whether to show the manage team link
 */
const TeamMemberSelect = ({ 
  value, 
  onChange, 
  teamMembers = [], 
  placeholder = "Select or type a name",
  className,
  onManageTeam,
  showManageLink = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [justSelected, setJustSelected] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Sync inputValue with external value
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    // Open dropdown when typing if there are team members
    if (teamMembers.length > 0 && !isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    // Don't open if we just selected something
    if (justSelected) {
      setJustSelected(false);
      return;
    }
    if (teamMembers.length > 0) {
      setIsOpen(true);
    }
  };

  const handleSelect = (name) => {
    setInputValue(name);
    onChange(name);
    setIsOpen(false);
    setJustSelected(true);
    // Reset justSelected after a short delay
    setTimeout(() => setJustSelected(false), 200);
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const filteredMembers = teamMembers.filter(member => 
    member.name?.toLowerCase().includes((inputValue || '').toLowerCase())
  );

  const hasTeamMembers = teamMembers.length > 0;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="flex gap-1">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            className="text-sm pr-8"
          />
          {hasTeamMembers && (
            <button
              type="button"
              onClick={toggleDropdown}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
            >
              <ChevronDown className={cn(
                "h-4 w-4 text-slate-400 transition-transform",
                isOpen && "rotate-180"
              )} />
            </button>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && hasTeamMembers && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredMembers.length > 0 ? (
            <>
              <div className="px-2 py-1.5 text-xs font-medium text-slate-500 bg-slate-50">
                Team Members
              </div>
              {filteredMembers.map((member, idx) => (
                <button
                  key={member.id || idx}
                  type="button"
                  onClick={() => handleSelect(member.name)}
                  className="w-full px-3 py-2 flex items-center gap-2 hover:bg-slate-100 text-left"
                >
                  <Users className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{member.name}</div>
                    {member.role && (
                      <div className="text-xs text-slate-500 truncate">{member.role}</div>
                    )}
                  </div>
                  {value === member.name && (
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                  )}
                </button>
              ))}
            </>
          ) : (
            <div className="px-3 py-4 text-center text-sm text-slate-500">
              No matching team members
            </div>
          )}
          
          {/* Manage Team Members option */}
          {showManageLink && onManageTeam && (
            <>
              <div className="border-t" />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setIsOpen(false);
                  onManageTeam();
                }}
                className="w-full px-3 py-2 flex items-center gap-2 hover:bg-blue-50 text-blue-600 text-sm"
              >
                <Plus className="h-4 w-4" />
                Add/Edit Team Members
              </button>
            </>
          )}
        </div>
      )}

      {/* Show helper when no team members */}
      {!hasTeamMembers && showManageLink && onManageTeam && (
        <p className="text-xs text-slate-400 mt-1">
          <button 
            type="button"
            onClick={onManageTeam}
            className="text-blue-500 hover:underline"
          >
            Add team members
          </button>
          {' '}in Project Settings for quick selection
        </p>
      )}
    </div>
  );
};

export default TeamMemberSelect;
